import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ComercializadorService } from '../../services/comercializador.service';
import { PaginacionSuperiorComponent } from '../../../productos-comercializadores/consultar-productos-comercializadores/components/control-paginacion/paginacion-superior';
import Swal from 'sweetalert2';
import { firstValueFrom } from 'rxjs';
import { IconoFiltroComponent } from 'src/app/common/components/icono-filtro/icono-filtro.component';
import { FiltrosComercializadores } from '../../interfaces/comercializadores-filtros.interface';
import { CaracterizacionFormateada } from '../../interfaces/caracterizacion-formateada.interface';
import { ComercializadorRow } from '../../interfaces/comercializador-row.interface';
import { VisualizarCaracterizacionEmpresaComponent } from './visualizar-caracterizacion-empresa/visualizar-caracterizacion-empresa.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SharedModule } from 'src/app/shared/shared.module';
import { etiquetaProductoTexto } from '../../utils/etiquetas-producto.util';



/** Respuesta paginada del servicio de comercializadores. */
interface PagedResponse {
    data?: ComercializadorRow[];
    items?: ComercializadorRow[];
    pagination?: { total?: number; totalRecords?: number; totalPages?: number };
    total?: number;
    totalPages?: number;
}

@Component({
    selector: 'app-clasificacion-comercializador',
    templateUrl: './clasificacion-comercializador.component.html',
    styleUrls: ['./clasificacion-comercializador.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        PaginacionSuperiorComponent,
        IconoFiltroComponent,
        SharedModule,
    ],
})
export class ClasificacionComercializadorComponent implements OnInit {
    public empresas: ComercializadorRow[] = [];
    public empresasSeleccionadas: Set<ComercializadorRow> = new Set();

    public filtros: FiltrosComercializadores = {
        nit: '',
        razonSocial: '',
        fechaAsignacion: '',
        tamanoComercializador: '',
    };

    public filtro = '';
    public filtroNit = '';
    public filtroRazonSocial = '';
    public filtroFechaAsignacion = '';
    public filtroTipo = '';

    public cargando = false;
    public mensaje = '';

    /* Variables de paginación */
    public paginaActual = 1;
    public totalPaginas = 0;
    public totalRegistros = 0;
    public limite = 10;
    public opcionesLimite: number[] = [10, 25, 50, 100];

    public tiposComercializador = [
        { id: 'Grande', nombre: 'Grande' },
        { id: 'Mediano', nombre: 'Mediano' },
        { id: 'Pequeño', nombre: 'Pequeño' },
    ];

    /**
     * Constructor del componente
     *
     * @param comercializadorService - Servicio para gestionar comercializadores
     * @param modalService - Servicio de modales de NgBootstrap
     */
    public constructor(
        private readonly comercializadorService: ComercializadorService,
        private readonly modalService: NgbModal
    ) {}

    /**
     * Carga inicial de datos al iniciar el componente
     */
    public ngOnInit(): void {
        this.cargarEmpresas();
    }

    /* Métodos de paginación */
    public onPaginaAnterior(): void {
        if (this.paginaActual > 1) {
            this.paginaActual--;
            this.cargarEmpresas();
        }
    }

    public onPaginaSiguiente(): void {
        if (this.paginaActual < this.totalPaginas) {
            this.paginaActual++;
            this.cargarEmpresas();
        }
    }

    public onCambiarLimite(nuevoLimite: number): void {
        this.limite = nuevoLimite;
        this.paginaActual = 1;
        this.cargarEmpresas();
    }

    /**
     * Filtra las empresas según el valor de la columna seleccionada.
     *
     * @param columna - El nombre de la columna por la cual se filtra (por ejemplo, 'nit', 'razonSocial', 'fechaAsignacion', 'tipo').
     * @param valor - El valor que se va a filtrar en la columna seleccionada.
     */
    public onFiltro(columna: string, valor: string): void {
        switch (columna) {
            case 'nit':
                this.filtros.nit = valor;
                break;
            case 'razonSocial':
                this.filtros.razonSocial = valor;
                break;
            case 'fechaAsignacion':
                this.filtros.fechaAsignacion = valor
                    ? this.convertirFecha(valor)
                    : '';
                break;
            case 'tipo':
                this.filtros.tamanoComercializador = valor;
                break;
        }

        this.paginaActual = 1;
        this.cargarEmpresas();
    }

    private convertirFecha(valor: string): string {
        // Espera formato dd/MM/yyyy
        const partes = valor.split('/');
        if (partes.length !== 3) {
            return '';
        }

        const [dia, mes, anio] = partes;

        return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
    }

    public onFiltroGeneralChange(): void {
        this.paginaActual = 1;
        this.cargarEmpresas();
    }

    public cargarEmpresas(): void {
        this.cargando = true;
        this.mensaje = 'Cargando empresas comercializadoras...';

        const filtrosParaServicio = {
            ...this.filtros,
        };

        this.comercializadorService
            .getPagedComercializadores(
                this.paginaActual.toString(),
                this.limite.toString(),
                filtrosParaServicio
            )
            .subscribe({
                next: (response) => {
                    response = this.aplicarCambiosPendientes(response);
                    this.procesarComercializadores(response);
                },
                error: (error: unknown) => {
                    // eslint-disable-next-line no-console
                    console.error('Error cargando comercializadores:', error);
                    this.cargando = false;
                    this.mensaje = 'Error al cargar las empresas';
                },
            });
    }

    /** Aplica los cambios pendientes a la respuesta de las empresas comercializadoras */
    public aplicarCambiosPendientes(response: any): any {
        this.empresasSeleccionadas.forEach((element) => {
            const empresa = response.data?.find((e) => e.id === element.id);
            if (empresa) {
                empresa.tamanoComercializador = element.tamanoComercializador;
            }
        });

        return response;
    }

    /**
     * Maneja el cambio de tamaño de un comercializador. Solo agrega a la lista
     * de empresas seleccionadas si el tamaño ha cambiado respecto al original.
     *
     * @param empresa empresa a la que se le cambia el tamaño
     * @param nuevoTamano valor del nuevo tamaño seleccionado
     */
    public onTamanoChange(
        empresa: ComercializadorRow,
        nuevoTamano: string
    ): void {
        // Solo agregar si el tamaño cambió respecto al original
        if (empresa.tamanoComercializadorOriginal !== nuevoTamano) {
            this.empresasSeleccionadas.add(empresa);
        } else {
            // Si vuelve al tamaño original, quitarla de la lista
            this.empresasSeleccionadas.delete(empresa);
        }
    }

    public limpiarSeleccion(): void {
        this.empresasSeleccionadas.clear();
    }

    /**
     * Procesa la respuesta del servicio de comercializadores,
     * actualiza la lista de empresas, agregando una propiedad con el tamaño original y los datos de paginación.
     *
     * @param response Respuesta del servicio con la lista de comercializadores
     */
    private procesarComercializadores(response: PagedResponse): void {
        const comercializadores = response.data ?? response.items ?? [];

        if (!comercializadores || comercializadores.length === 0) {
            this.empresas = [];
            this.cargando = false;
            this.mensaje = 'No se encontraron empresas comercializadoras';
            this.totalRegistros = 0;
            this.totalPaginas = 0;
            return;
        }

        /** Se agrega una propiedad temporal para mantener el valor original del tamaño del comercializador
         */
        comercializadores.forEach((comercializador) => {
            comercializador.tamanoComercializadorOriginal =
                comercializador.tamanoComercializador;
        });

        /* Extraer información de paginación */
        if (response.pagination) {
            this.totalRegistros =
                response.pagination.total ||
                response.pagination.totalRecords ||
                0;
            this.totalPaginas =
                response.pagination.totalPages ||
                Math.ceil(this.totalRegistros / this.limite);
        } else if (response.total !== undefined) {
            this.totalRegistros = response.total;
            this.totalPaginas =
                response.totalPages ||
                Math.ceil(this.totalRegistros / this.limite);
        } else {
            this.totalRegistros = comercializadores.length;
            this.totalPaginas = 1;
        }

        this.empresas = comercializadores;

        this.cargando = false;
        this.mensaje = `Mostrando ${this.empresas.length} de ${this.totalRegistros} empresas comercializadoras`;
    }

    public async guardar(): Promise<void> {
        if (this.empresasSeleccionadas.size === 0) {
            this.mensaje =
                'No hay empresas con nuevo tamaño asignado para guardar';
            Swal.fire({
                title: 'Sin cambios',
                text: 'No hay empresas con nuevo tamaño asignado para guardar',
                icon: 'info',
                timer: 3000,
            });
            return;
        }

        await this.guardarTodasLasEmpresas(
            Array.from(this.empresasSeleccionadas)
        );
    }

    /*
     * Envía al backend todas las clasificaciones válidas de empresas comercializadoras
     * para guardar su tamaño asignado. Maneja la respuesta y muestra notificaciones al usuario.
     */
    private async guardarTodasLasEmpresas(
        empresasValidas: ComercializadorRow[]
    ): Promise<void> {
        /**
         * Se envián solamente los ids y tamaños de las empresas válidas.
         */
        const asignaciones = empresasValidas.map((empresa) => ({
            id: empresa.id.toString(),
            tamanoComercializador: empresa.tamanoComercializador,
        }));

        try {
            this.cargando = true;
            this.mensaje = 'Guardando clasificaciones...';

            const response = await firstValueFrom(
                this.comercializadorService.asignarTamano(asignaciones)
            );

            this.limpiarSeleccion();

            this.cargando = false;

            /**
             * Manejo de la respuesta del guardado.
             */
            let mensajeHtml = '';
            const clasificacionesConMensaje = response.resultados.filter(
                (e) => e.estado === 'guardado_sin_notificar'
            );
            if (response.fallidos === 0) {
                // Caso 100% exitoso
                mensajeHtml += `
                    <p class="text-success">Todas las empresas de esta página se guardaron correctamente.</p>
                    ${
                        clasificacionesConMensaje.length > 0
                            ? `
                        <div class="alert alert-warning mt-2">
                            <p><strong>${clasificacionesConMensaje.length} empresas</strong> guardadas con novedad:</p>
                            <ul style="font-size: 0.9em;">
                                ${clasificacionesConMensaje
                                    .map((e) => `<li>${e.mensaje}</li>`)
                                    .join('')}
                            </ul>
                        </div>
                        `
                            : ''
                    }
                `;
            } else if (response.exitosos > 0) {
                // Caso parcial: algunos éxitos y algunos errores
                mensajeHtml += `
                    <p class="text-warning">Se procesaron parcialmente las empresas de esta página.</p>
                    <div class="mt-2">
                        <p class="text-success"><strong>${response.exitosos} empresas guardadas:</strong></p>
                        <ul style="font-size: 0.9em;">
                            ${response.resultados
                                .map((e) => `<li>${e.mensaje}</li>`)
                                .join('')}
                        </ul>
                        
                        <p class="text-danger mt-3"><strong>${response.fallidos} empresas con errores:</strong></p>
                        <ul style="font-size: 0.9em;">
                            ${response.errores
                                .map((err) => `<li>${err}</li>`)
                                .join('')}
                        </ul>
                    </div>
                `;
            } else {
                // Caso: todos fallaron
                mensajeHtml += `
                    <p class="text-danger">No se pudo guardar ninguna empresa.</p>
                    <div class="mt-2">
                        <p><strong>Errores encontrados:</strong></p>
                        <ul style="font-size: 0.9em;">
                            ${response.errores
                                .map((err) => `<li>${err}</li>`)
                                .join('')}
                        </ul>
                    </div>
                `;
            }

            Swal.fire({
                title:
                    response.fallidos > 0
                        ? 'Guardado parcial'
                        : 'Clasificación exitosa',
                html: mensajeHtml,
                icon: response.fallidos > 0 ? 'info' : 'success',
                width: '700px',
            });

            this.mensaje = `${asignaciones.length} clasificaciones guardadas correctamente`;

            // Recargar para ver cambios
            setTimeout(() => {
                this.cargarEmpresas();
            }, 1000);
        } catch (error: unknown) {
            this.cargando = false;
            // eslint-disable-next-line no-console
            console.error('Error guardando las clasificaciones:', error);
        }
    }

    /*
     * Muestra la caracterización registrada de una empresa comercializadora,
     * validando si los datos están completos o parciales.
     */
    public verCaracterizacion(datosCaracterizacion: ComercializadorRow): void {
        this.comercializadorService
            .getComercializador(datosCaracterizacion.id)
            .subscribe({
                next: (response) => {
                    const datosConDetalle = {
                        ...datosCaracterizacion,
                        detalleCaracterizacion: response,
                    };
                    this.mostrarModalCaracterizacion(
                        datosCaracterizacion.agenteVigilado,
                        datosConDetalle
                    );
                },
                error: (error: unknown) => {
                    // eslint-disable-next-line no-console
                    console.error('Error cargando comercializador:', error);
                    this.cargando = false;
                    this.mensaje = 'Error al cargar las comercializador';
                },
            });
    }

    private async mostrarModalCaracterizacion(
        empresa: { nit?: string; razonSocial?: string; tipo?: string },
        caracterizacion: ComercializadorRow & {
            detalleCaracterizacion?: unknown;
        }
    ): Promise<void> {
        this.cargando = true;

        try {
            const modalRef = this.modalService.open(
                VisualizarCaracterizacionEmpresaComponent,
                { size: 'lg', centered: true, scrollable: true }
            );
            modalRef.componentInstance.empresa = empresa;
            modalRef.componentInstance.caracterizacion =
                await this.formatearDatosCaracterizacion(caracterizacion);
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.error('Error formateando datos:', error);
            Swal.fire({
                title: 'Error',
                text: 'Error al cargar los detalles de la caracterización',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        } finally {
            this.cargando = false;
        }
    }

    private async formatearDatosCaracterizacion(
        caracterizacion: ComercializadorRow & {
            detalleCaracterizacion?: unknown;
        }
    ): Promise<CaracterizacionFormateada> {
        const detalle = (caracterizacion?.detalleCaracterizacion ??
            caracterizacion) as Record<string, unknown>;
        const id = (key: string, snakeKey: string): number | undefined =>
            (detalle?.[key] ?? detalle?.[snakeKey]) as number | undefined;

        try {
            const [
                puntosVentaNombre,
                puntosVentaMunicipiosNombre,
                puntosVentaCapitalesNombre,
                personalEmpresaNombre,
                clasificacionPersonalNombre,
                montoPromedioNombre,
                porcentajeVentasNombre,
                puntosVentaProductosNombre,
            ] = await Promise.all([
                this.cargarNombrePorId(
                    'puntos-venta',
                    id('puntosVentaId', 'puntos_venta_id') ?? 0
                ),
                this.cargarNombrePorId(
                    'puntos-venta-municipios',
                    id(
                        'puntosVentaMunicipiosId',
                        'puntos_venta_municipios_id'
                    ) ?? 0
                ),
                this.cargarNombrePorId(
                    'puntos-venta-capitales',
                    id('puntosVentaCapitalesId', 'puntos_venta_capitales_id') ??
                        0
                ),
                this.cargarNombrePorId(
                    'personal-empresa',
                    id('personalEmpresaId', 'personal_empresa_id') ?? 0
                ),
                this.cargarNombrePorId(
                    'clasificacion-personal-empresa',
                    id(
                        'clasificacionPersonalEmpresaId',
                        'clasificacion_personal_empresa_id'
                    ) ?? 0
                ),
                this.cargarNombrePorId(
                    'monto-promedio-ventas',
                    id('montoPromedioVentasId', 'monto_promedio_ventas_id') ?? 0
                ),
                this.cargarNombrePorId(
                    'porcentaje-ventas',
                    id('porcentajeVentasId', 'porcentaje_ventas_id') ?? 0
                ),
                this.cargarNombrePorId(
                    'puntos-venta-productos',
                    id('puntosVentaProductosId', 'puntos_venta_productos_id') ??
                        0
                ),
            ]);

            const gruposProductos = etiquetaProductoTexto(
                (detalle?.gruposProductos as string | undefined) ??
                    (detalle?.grupos_productos as string | undefined) ??
                    'No registrado'
            );
            const tamanoComercializador =
                (detalle?.tamanoComercializador as string | null | undefined) ??
                (detalle?.tamano_comercializador as
                    | string
                    | null
                    | undefined) ??
                null;
            const fechaAsignacion =
                (detalle?.fechaAsignacion as string | null | undefined) ??
                (detalle?.fecha_asignacion as string | null | undefined) ??
                null;

            return {
                gruposProductos,
                puntosVenta: puntosVentaNombre || 'No registrado',
                puntosVentaMunicipios:
                    puntosVentaMunicipiosNombre || 'No aplica',
                puntosVentaCapitales: puntosVentaCapitalesNombre || 'No aplica',
                personalEmpresa: personalEmpresaNombre || 'No registrado',
                clasificacionPersonal:
                    clasificacionPersonalNombre || 'No aplica',
                montoPromedio: montoPromedioNombre || 'No registrado',
                porcentajeVentas: porcentajeVentasNombre || 'No registrado',
                puntosVentaProductos:
                    puntosVentaProductosNombre || 'No registrado',
                tamanoComercializador,
                fechaAsignacion,
            };
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.error('Error formateando datos:', error);
            return this.obtenerDatosPorDefecto();
        }
    }

    private obtenerDatosPorDefecto(): CaracterizacionFormateada {
        return {
            gruposProductos: 'No disponible',
            puntosVenta: 'No disponible',
            puntosVentaMunicipios: 'No disponible',
            puntosVentaCapitales: 'No disponible',
            personalEmpresa: 'No disponible',
            clasificacionPersonal: 'No disponible',
            montoPromedio: 'No disponible',
            porcentajeVentas: 'No disponible',
            puntosVentaProductos: 'No disponible',
            tamanoComercializador: null,
            fechaAsignacion: null,
        };
    }

    private async cargarNombrePorId(tipo: string, id: number): Promise<string> {
        if (!id) return '';

        try {
            const datos = await firstValueFrom(
                this.comercializadorService.getPreguntasComercializador(tipo)
            );
            const item = datos.find(
                (d: { id: number; nombre?: string }) => d.id === id
            );
            return item?.nombre ?? '';
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.error(`Error cargando ${tipo}:`, error);
            return '';
        }
    }
}
